import { useState, useRef, useEffect } from 'react';
import { urlReportInApp } from '../lib/condivisione';
import { parseIndirizzo, tipoNome, luoghi } from '../lib/tipologia';
import { PROSPECT_VC, PROSPECT_VC_KEY, VLABEL, CardChars } from './prospectShared';

// Foto Street View a tutta larghezza in cima alla card (stile immobiliare.it).
// Bleed fuori dal padding della card (24px radius in alto). Marina 2026-07-13.
function CardPhoto({ row, onPhoto }) {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const [err, setErr] = useState(false);
  const lat = row?.lat, lng = row?.lng;
  const has = !!key && lat != null && lng != null && !err;
  const src = has ? `https://maps.googleapis.com/maps/api/streetview?size=640x360&location=${lat},${lng}&fov=80&key=${key}` : null;
  const click = (onPhoto && has) ? (e) => { e.stopPropagation(); onPhoto({ lat, lng, address: row?.indirizzo }); } : undefined;
  return (
    <div onClick={click} style={{
      margin: '-16px -16px 12px', height: 168, borderRadius: '24px 24px 0 0', overflow: 'hidden', position: 'relative',
      backgroundImage: 'repeating-linear-gradient(45deg,#EEEDE9 0 10px,#E5E4DF 10px 20px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: click ? 'pointer' : 'default',
    }}>
      {src
        ? <img src={src} alt="" loading="lazy" onError={() => setErr(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: '#9AA0A6', letterSpacing: '0.04em' }}>foto non disponibile</span>}
    </div>
  );
}

/**
 * Card immobile prospect UNICA — usata su mappa (carousel), Analizzati e Home.
 * La mappa passa lo stato di analisi live (`analysis`) per l'animazione; gli altri
 * contesti la lasciano null e la card mostra lo stato done/idle. 2026-07-13.
 *
 * Props:
 *  - row              dati immobile
 *  - width            larghezza css (default '100%'; la mappa passa cardW+'px')
 *  - analysis         oggetto analisi live {phase,cfgKey,shown,score,scanSub} o null
 *  - analyzed         bool: analisi completata (verdetto disponibile)
 *  - onToggleFav(row) toggle preferito (il parent aggiorna row.preferito)
 *  - onAnalyze(row)   "Avvia analisi AI"
 *  - onReport(url)    "Scopri il perché" → apre il report
 *  - onShare(row)     "Condividi" → link pubblico al report (facoltativo: senza
 *                     questa prop il tasto non compare)
 *  - onOpen(row)      click sul corpo card (navigazione); se assente card non cliccabile
 *  - onClose()        pulsante × (solo mappa)
 */
export default function ProspectCard({
  row, width = '100%', analysis = null, analyzed = false,
  onToggleFav, onAnalyze, onAnalyzePvp, onReport, onShare, onOpen, onClose, onPhoto,
  selfReveal = false,   // Home/Analizzati: la card esegue da sé l'animazione di reveal
}) {
  const r = row;
  const hasMin = r.offerta_minima != null && Number(r.offerta_minima) > 0;
  const price = hasMin ? r.offerta_minima : r.prezzo_base;

  // --- Animazione di reveal LOCALE (usata quando selfReveal, es. Home) --------
  // Sulla mappa l'animazione la guida il parent via `analysis`; qui la esegue la
  // card così il click NON naviga e c'è il reveal come sulla mappa. 2026-07-14.
  const [localAnim, setLocalAnim] = useState(null);
  const [localDone, setLocalDone] = useState(false);
  const [stallTip, setStallTip] = useState(false);   // fumetto "ci vuole più tempo"
  const revealTimers = useRef([]);
  useEffect(() => () => revealTimers.current.forEach(clearTimeout), []);
  const runReveal = (rowToReveal) => {
    const cfgKey = PROSPECT_VC_KEY(rowToReveal.rivelasta_verdetto);
    const cfg = PROSPECT_VC[cfgKey];
    if (!cfg) { setLocalDone(true); return; }
    const T = (fn, ms) => revealTimers.current.push(setTimeout(fn, ms));
    setLocalAnim({ rowId: rowToReveal.id, cfgKey, phase: 'scan', scanSub: 0, shown: [] });
    T(() => setLocalAnim((s) => s ? { ...s, scanSub: 1 } : s), 900);
    T(() => setLocalAnim((s) => s ? { ...s, scanSub: 2 } : s), 1700);
    T(() => {
      setLocalAnim((s) => s ? { ...s, phase: 'accumulate', shown: [] } : s);
      const revealFactor = (idx) => {
        const f = cfg.factors[idx];
        setLocalAnim((s) => s ? { ...s, shown: [...s.shown, f] } : s);
        if (idx + 1 < cfg.factors.length) T(() => revealFactor(idx + 1), 900);
        else T(() => {
          setLocalAnim((s) => s ? { ...s, phase: 'reveal' } : s);
          setLocalDone(true);
          T(() => setLocalAnim(null), 1700);
        }, 1000);
      };
      revealFactor(0);
    }, 2400);
  };

  // Animazione "che si BLOCCA": scan → la barra si ferma a metà (mai reveal).
  // Usata quando l'analisi è ancora da fare (skill astemaster, verdetto via
  // WhatsApp): parte l'animazione ma resta grigia e compare il fumetto. 2026-07-14.
  const runStall = (rowToStall) => {
    const T = (fn, ms) => revealTimers.current.push(setTimeout(fn, ms));
    setLocalAnim({ rowId: rowToStall.id, cfgKey: null, phase: 'scan', scanSub: 0, shown: [] });
    T(() => setLocalAnim((s) => s ? { ...s, scanSub: 1 } : s), 900);
    T(() => setLocalAnim((s) => s ? { ...s, scanSub: 2 } : s), 1700);
    T(() => setLocalAnim((s) => s ? { ...s, phase: 'stalling' } : s), 2500);
  };

  // Stato effettivo (esterno dalla mappa OPPURE locale in self-reveal)
  const activeAnim = analysis || localAnim;
  const anActive = !!activeAnim;
  // "done" SOLO se c'è davvero un verdetto/report da mostrare. Un'analisi registrata
  // ma ancora in corso o bloccata (nessun verdetto) NON è "done": deve cedere il
  // passo allo stato "in corso" (job) o "da analizzare", non al blocco morto
  // "Analisi non disponibile". Marina 2026-07-14.
  const hasVerdict = !!(r.rivelasta_verdetto || r.url_report);
  const isDone = (analyzed || localDone) && hasVerdict;
  const jobBusy = r.job_status === 'running' || r.job_status === 'queued';

  // Il fumetto compare ~0.9s DOPO che la barra si è bloccata (fine animazione).
  useEffect(() => {
    if (activeAnim && activeAnim.phase === 'stalling') {
      const id = setTimeout(() => setStallTip(true), 900);
      return () => clearTimeout(id);
    }
    setStallTip(false);
  }, [activeAnim && activeAnim.phase]);

  // Se sulla mappa arriva l'animazione ESTERNA (verdetto reale via poll) mentre
  // la card è bloccata in "stalling", cede il passo al reveal esterno.
  useEffect(() => {
    if (analysis && localAnim && localAnim.phase === 'stalling') { setLocalAnim(null); setStallTip(false); }
  }, [analysis, localAnim]);

  // Trigger del bottone "Avvia analisi AI":
  //  - mappa/Home: il parent fa cap-check/registrazione e ritorna la modalità:
  //    'reveal' (verdetto noto → animazione+reveal locale)
  //    'requested' (analisi da fare → animazione che si BLOCCA + fumetto)
  //    'external' (la mappa guida l'animazione dall'esterno via `analysis`) | false
  const triggerAnalyze = async () => {
    if (!selfReveal) { const ext = onAnalyze || (r.url_pvp ? onAnalyzePvp : null); if (ext) ext(r); return; }
    const mode = onAnalyze ? await onAnalyze(r) : (r.rivelasta_verdetto ? 'reveal' : 'requested');
    if (mode === 'reveal') runReveal(r);
    else if (mode === 'requested') runStall(r);
    // 'external' / false → nessuna animazione locale
  };

  const { via, localita } = parseIndirizzo(r.indirizzo);

  return (
    <article className="pr-card" style={{ width, flex: 'none', position: 'relative', cursor: onOpen ? 'pointer' : 'default' }}
             onClick={onOpen ? () => onOpen(r) : undefined}>
      <CardPhoto row={r} onPhoto={onPhoto} />

      {onClose && (
        <button className="pr-card__close" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Chiudi"
                style={{ position: 'absolute', top: 12, right: 12, zIndex: 3, background: 'rgba(255,255,255,0.92)', boxShadow: '0 1px 6px rgba(0,0,0,0.25)' }}>×</button>
      )}

      {(price != null || onToggleFav) && (
        <div className="pr-price-row" style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="pr-price-row__val" style={{ whiteSpace: 'nowrap', flex: 1 }}>
            {price != null ? '€ ' + Number(price).toLocaleString('it-IT', { maximumFractionDigits: 0 }) : ''}
          </div>
          {onToggleFav && (
            <button onClick={(e) => { e.stopPropagation(); onToggleFav(r); }} aria-label="Salva nei preferiti"
                    style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 2, lineHeight: 0, flex: '0 0 auto' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill={r.preferito ? '#DC2626' : 'none'} stroke={r.preferito ? '#DC2626' : '#9AA0A6'} strokeWidth="1.9" strokeLinejoin="round">
                <path d="M12 21s-7-4.35-9.5-8.5C1 9.5 2.5 5.5 6.5 5.5c2 0 3.5 1.5 5.5 3.5 2-2 3.5-3.5 5.5-3.5 4 0 5.5 4 4 7C19 16.65 12 21 12 21z" />
              </svg>
            </button>
          )}
        </div>
      )}

      <div style={{ marginTop: price != null ? 3 : 0, fontSize: 13, fontWeight: 800, color: '#1F2937', lineHeight: 1.35, letterSpacing: '-0.01em' }}>
        {[tipoNome(r.tipologia), ...luoghi(r.comune, r.zona || localita)].join(', ')}
        {via && <span style={{ fontWeight: 600, color: '#6B7280' }}> · {via}</span>}
      </div>

      <CardChars row={r} />

      {/* Box verdetto (SEMPRE presente): grigio idle → tinta del verdetto quando done,
          con bar+chip IN CIMA (il cuore è stato spostato sul prezzo). +
          Bottone (SEMPRE presente, 50px): NERO "Avvia analisi AI" → animazione →
          COLORE DEL VERDETTO "Scopri il perché". Entrambi persistono → nessuno shift. */}
      {(() => {
        const phase = anActive ? activeAnim.phase : isDone ? 'done' : jobBusy ? 'job' : 'idle';
        const resolved = phase === 'reveal' || phase === 'done';
        const analyzing = (anActive && phase !== 'reveal') || phase === 'job';
        const cfgKey = anActive ? activeAnim.cfgKey : (isDone ? PROSPECT_VC_KEY(r.rivelasta_verdetto) : null);
        const cfg = cfgKey ? PROSPECT_VC[cfgKey] : null;
        const dot = cfg ? cfg.dot : '#16915E';
        const total = cfg ? cfg.factors.length : 6;
        const shown = anActive ? activeAnim.shown : [];
        const progFrac = phase === 'idle' ? 0
          : phase === 'job' ? 0.4
          : phase === 'scan' ? (0.08 + (activeAnim.scanSub || 0) * 0.09)
          : phase === 'accumulate' ? (0.34 + 0.66 * (shown.length / total))
          : phase === 'stalling' ? 0.62   // la barra si FERMA qui (mai 100%)
          : 1;
        const barFill = resolved ? 100 : Math.round(progFrac * 100);
        const verdictTint = cfgKey === 'ottimo' ? 'rgba(22,145,94,.07)' : cfgKey === 'buono' ? 'rgba(201,138,30,.08)' : cfgKey === 'evita' ? 'rgba(194,69,59,.07)' : null;
        const chipText = resolved ? (VLABEL[r.rivelasta_verdetto] || (cfg ? cfg.ai : 'Analizzato'))
          : (phase === 'idle' ? 'Da analizzare' : 'Analizzo…');
        const doAnalyze = onAnalyze || (r.url_pvp ? onAnalyzePvp : null);
        const message = phase === 'scan'
            ? (['Leggo la perizia del tribunale…', 'Verifico le difformità urbanistiche…', 'Analizzo i dati del tribunale…'][activeAnim.scanSub] || 'Leggo la perizia del tribunale…')
          : phase === 'accumulate'
            ? (shown.length < 2 ? 'Analizzo 247 fattori di rischio…' : shown.length < 4 ? 'Valuto domanda e liquidità di zona…' : 'Calcolo lo score finale…')
          : phase === 'stalling'
            ? 'Analisi approfondita in corso…'
          : phase === 'job'
            ? 'Analisi in corso…'
          : 'Analisi completata';

        return (
          <div style={{ position: 'relative' }}>
          {stallTip && (
            <div style={{
                   position: 'absolute', bottom: 'calc(100% + 10px)', left: 6, right: 6, zIndex: 6,
                   background: '#1F2530', color: '#fff', borderRadius: 14, padding: '11px 13px',
                   fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, boxShadow: '0 12px 30px rgba(0,0,0,0.34)',
                   animation: 'pr-msg-in 260ms ease-out both',
                 }}>
              <span style={{ marginRight: 6 }}>⏳</span>
              Questa analisi è più approfondita: la stiamo elaborando e ti avvisiamo su <b>WhatsApp</b> appena è pronta.
              <button onClick={(e) => { e.stopPropagation(); setStallTip(false); }}
                      style={{
                        marginTop: 10, width: '100%', border: 0, background: 'rgba(255,255,255,0.14)',
                        color: '#fff', borderRadius: 9, padding: '8px', fontWeight: 800, fontSize: 12.5,
                        cursor: 'pointer',
                      }}>Ho capito</button>
              <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '9px solid #1F2530' }} />
            </div>
          )}
          <div className="pr-verdict" style={{
            position: 'relative', overflow: 'hidden',
            background: resolved && verdictTint ? verdictTint : '#F5F4F0',
            border: '1px solid ' + (resolved ? dot + '44' : 'transparent'),
            transition: 'border-color .4s, background .4s',
          }}>
            {/* bar + chip IN CIMA */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 26 }}>
              <div className="pr-verdict__bar" style={{ flex: 1, margin: 0 }}>
                <div className="pr-verdict__bar-fill" style={{ width: `${barFill}%`, background: resolved ? dot : '#B7B5AC', transition: 'width .4s ease, background .4s' }} />
              </div>
              <span className="pr-verdict__pill" style={{ flex: '0 0 auto', color: resolved ? '#fff' : '#9A9EA4', background: resolved ? dot : '#E7E6E1', transition: 'background .4s' }}>{chipText}</span>
            </div>

            {/* Bottone DENTRO il blocco */}
            {phase === 'idle' && doAnalyze ? (
              <button className="pr-cardbtn pr-cardbtn--analyze" onClick={(e) => { e.stopPropagation(); triggerAnalyze(); }}>
                <span className="pr-wand">
                  <svg className="pr-wand__main" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3.5 20.5L13 11" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><path d="M16 3.8L17.4 6.6L20.2 8L17.4 9.4L16 12.2L14.6 9.4L11.8 8L14.6 6.6Z" fill="#fff"/></svg>
                  <svg className="pr-wand__twinkle" width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M12 3L13.4 9.6L20 11L13.4 12.4L12 19L10.6 12.4L4 11L10.6 9.6Z" fill="#4FD89E"/></svg>
                </span>
                <span>Avvia analisi AI</span>
              </button>
            ) : analyzing ? (
              <div className="pr-cardbtn pr-cardbtn--analyzing">
                <div className="pr-cardbtn__spinner" />
                <span key={phase + '-' + message} className="pr-cardbtn__msg">{message}</span>
              </div>
            ) : r.url_report ? (
              /* «Scopri il perché» resta il gesto principale; «Condividi» gli sta
                 accanto, più discreto, e compare solo se il parent passa onShare.
                 Il tasto era stato messo per errore nelle card a stringa della
                 mappa, che il prospect non usa: Michael non l'ha mai visto
                 (regressione del 16/09/2026). */
              <div style={{ display: 'flex', gap: 6, width: '100%' }}>
                <button className="pr-cardbtn pr-cardbtn--report" style={{ '--glow-color': dot, flex: 1 }}
                        onClick={(e) => { e.stopPropagation(); onReport && onReport(urlReportInApp(r.id)); }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M7 3h7l5 5v13H7z" stroke="#16181C" strokeWidth="1.8" strokeLinejoin="round"/><path d="M14 3v5h5M9.5 13h5M9.5 16.5h5" stroke="#16181C" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  <span>Scopri il perché</span>
                </button>
                {onShare && (
                  <button className="pr-cardbtn pr-cardbtn--share"
                          title="Condividi il report"
                          aria-label="Condividi il report"
                          onClick={(e) => { e.stopPropagation(); onShare(r, e.currentTarget); }}
                          style={{ flex: '0 0 auto', width: 46, padding: 0, justifyContent: 'center' }}>
                    {/* icona di condivisione: nodo con due rami, quella di sistema */}
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                      <circle cx="18" cy="5" r="2.6" stroke="#16181C" strokeWidth="1.8"/>
                      <circle cx="6" cy="12" r="2.6" stroke="#16181C" strokeWidth="1.8"/>
                      <circle cx="18" cy="19" r="2.6" stroke="#16181C" strokeWidth="1.8"/>
                      <path d="M15.7 6.3L8.3 10.7M8.3 13.3l7.4 4.4" stroke="#16181C" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              <div className="pr-cardbtn" style={{ background: 'transparent', color: 'var(--pr-fg-faint)', fontWeight: 600, fontSize: 13 }}>Analisi non disponibile</div>
            )}
            {phase === 'reveal' && <div className="pr-verdict__flash" />}
          </div>
          </div>
        );
      })()}
    </article>
  );
}
